'use client';

import { Box, Container, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { ComponentDataHomepageContent } from '@/types';

import { newHomepageTypography } from './newHomepageStyles';

const DEFAULT_EASE_CARDS = [
  {
    title: '80+ Lenders, One Place',
    body: "Access Australia's largest network of commercial lenders without the legwork.",
    iconSrc: '/assets/new-homepage/icons8-money-transfer.svg',
  },
  {
    title: 'Know Your Max Limit',
    body: 'Get a real-time calculation of your maximum borrowing power across every loan type.',
    iconSrc: '/assets/new-homepage/icons8-flash-on.svg',
  },
  {
    title: 'Matched for Approval',
    body: 'We run your profile through our data-rich, proven matching-engine containing thousands of lender policies in order find the best options for you.',
    iconSrc: '/assets/new-homepage/icons8-check-file.svg',
  },
] as const;

interface BorrowingMadeEasySectionProps {
  cms?: ComponentDataHomepageContent | null;
}

export function BorrowingMadeEasySection({ cms }: BorrowingMadeEasySectionProps = {}) {
  const theme = useTheme();

  const headingMain = cms?.BmeHeadingMain || 'Borrowing';
  const headingAccent = cms?.BmeHeadingAccent || 'made easy.';
  const subtitle =
    cms?.BmeSubtitle ||
    "Smart technology, Australia's biggest lender network, and instant clarity on your borrowing power - all in one place.";

  const cards =
    cms?.BmeCards && cms.BmeCards.length > 0
      ? cms.BmeCards.map((card, i) => ({
          title: card?.Title || DEFAULT_EASE_CARDS[i]?.title || '',
          body: card?.Body || DEFAULT_EASE_CARDS[i]?.body || '',
          iconSrc:
            getImageUrl({ image: card?.Icon as any, size: 'url' }) ||
            DEFAULT_EASE_CARDS[i]?.iconSrc ||
            '',
        }))
      : DEFAULT_EASE_CARDS.map((c) => ({ title: c.title, body: c.body, iconSrc: c.iconSrc }));

  return (
    <Box component="section" sx={{ py: { xs: 7, md: 10 }, background: theme.palette.common.white }}>
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography
            component="h2"
            sx={{ color: theme.palette.brand.bodyText, ...newHomepageTypography.h2, mb: 2 }}
          >
            {headingMain}{' '}
            <Box component="span" sx={{ color: theme.palette.brand.primary }}>
              {headingAccent}
            </Box>
          </Typography>
          <Typography
            sx={{
              maxWidth: 520,
              mx: 'auto',
              color: theme.palette.brand.bodyText,
              fontSize: { xs: 15, md: 16 },
              lineHeight: 1.45,
            }}
          >
            {subtitle}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: 2.5, md: 3 },
          }}
        >
          {cards.map((card) => (
            <Box
              key={card.title}
              sx={{
                minHeight: { xs: 286, md: 374 },
                borderRadius: { xs: 2.5, md: 3 },
                background: '#f8f8fa',
                px: { xs: 3, sm: 4, md: 5 },
                pt: { xs: 4, md: 5.5 },
                pb: { xs: 4, md: 5 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                boxShadow: '0 12px 34px rgba(32, 11, 66, 0.035)',
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  mb: { xs: 3, md: 4.5 },
                  display: 'grid',
                  placeItems: 'center',
                  filter: `drop-shadow(0 16px 24px ${alpha(theme.palette.brand.primary, 0.2)})`,
                  '& img': { display: 'block', width: '100%', height: '100%' },
                }}
              >
                <Box component="img" src={card.iconSrc} alt="" aria-hidden="true" />
              </Box>
              <Typography
                component="h3"
                sx={{
                  width: '100%',
                  color: theme.palette.brand.primary,
                  ...newHomepageTypography.h4,
                  minHeight: { xs: '52px', md: '63px' },
                  mb: { xs: 2.2, md: 3 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                {card.title}
              </Typography>
              <Typography
                sx={{
                  width: '100%',
                  maxWidth: { md: 327 },
                  color: 'brand.bodyText',
                  ...newHomepageTypography.body16,
                  textAlign: 'left',
                }}
              >
                {card.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
